/**
 * Zustand store — настройки приложения.
 * Persist middleware сохраняет в localStorage.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { safeLocalStorage, safeSessionStorage } from '../utils/storage';

import type { BotConfig } from '@tg-miniapp/shared-types';

/** Роль текущего пользователя в Mini App */
export type UserRole = 'owner' | 'manager' | 'assistant' | 'master' | 'admin' | 'client';

export interface UserMembership {
  businessId: string;
  businessName: string;
  role: UserRole;
  workerId: string | null;
  permissions: any;
}

export interface FeatureFlag {
  key: string;
  name: string;
  description: string;
  isEnabled: boolean;
  canaryPercentage: number;
  allowedRoles: string[];
  betaOnly: boolean;
}

interface AppSettingsState {
  theme: 'auto' | 'light' | 'dark';
  language: 'ru' | 'en';
  notificationsEnabled: boolean;
  lastSyncAt: number;
  currentRole: UserRole;
  currentBusinessId: string | null;
  currentWorkerId: string | null;
  onboardingComplete: boolean;
  config: BotConfig | null;
  isClientMode: boolean; // Включение B2C клиентского режима для тестирования и клиентов
  realRole: UserRole | null; // Реальная роль из БД
  memberships: UserMembership[];
  userId: string | null;
  sandboxMockUserId: string | null;
  termsAccepted: boolean;
  featureFlags: FeatureFlag[];
}

interface AppSettingsActions {
  setTheme: (theme: AppSettingsState['theme']) => void;
  setLanguage: (language: AppSettingsState['language']) => void;
  setCurrentRole: (role: UserRole) => void;
  setCurrentBusinessId: (id: string | null) => void;
  setCurrentWorkerId: (id: string | null) => void;
  setOnboardingComplete: (complete: boolean) => void;
  setLastSyncAt: (timestamp: number) => void;
  setConfig: (config: BotConfig | null) => void;
  setIsClientMode: (isClient: boolean) => void; // Метод переключения клиентского режима
  setRealRole: (role: UserRole | null) => void; // Метод установки реальной роли
  setMemberships: (memberships: UserMembership[]) => void;
  setUserId: (userId: string | null) => void;
  setSandboxMockUserId: (id: string | null) => void;
  switchContext: (businessId: string | null, role: UserRole, workerId: string | null) => void;
  setTermsAccepted: (accepted: boolean) => void;
  setFeatureFlags: (flags: FeatureFlag[]) => void;
}

export const useAppSettings = create<AppSettingsState & AppSettingsActions>()(
  persist(
    (set) => ({
      // State
      theme: 'auto',
      language: 'ru',
      notificationsEnabled: true,
      lastSyncAt: 0,
      currentRole: 'owner',
      currentBusinessId: null,
      currentWorkerId: null,
      onboardingComplete: false,
      config: null,
      isClientMode: false,
      realRole: null,
      memberships: [],
      userId: null,
      sandboxMockUserId: null,
      termsAccepted: false,
      featureFlags: [],

      // Actions — pure setters, no side effects
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
      setCurrentRole: (currentRole) => set((state) => {
        const isBypass = typeof window !== 'undefined' && (
          window.location.hostname === 'localhost' || 
          window.location.hostname === '127.0.0.1' ||
          safeSessionStorage.getItem('sandbox_admin_bypass') === 'true' || 
          new URLSearchParams(window.location.search).get('sandbox') === 'true'
        );
        if (isBypass && typeof window !== 'undefined') {
          safeSessionStorage.setItem('sandbox_admin_bypass', 'true');
        }
        // Strict Security Guard: Block setting currentRole to 'admin' if realRole is already loaded and is not 'admin'
        if (!isBypass && currentRole === 'admin' && state.realRole && state.realRole !== 'admin') {
          return {};
        }
        return { 
          currentRole
        };
      }),
      setCurrentBusinessId: (currentBusinessId) => set((state) => ({ 
        currentBusinessId
      })),
      setCurrentWorkerId: (currentWorkerId) => set({ currentWorkerId }),
      setOnboardingComplete: (onboardingComplete) => set({ onboardingComplete }),
      setLastSyncAt: (lastSyncAt) => set({ lastSyncAt }),
      setConfig: (config) => set({ config }),
      setIsClientMode: (isClientMode) => set({ isClientMode }),
      setRealRole: (realRole) => set({ realRole }),
      setMemberships: (memberships) => set({ memberships }),
      setUserId: (userId) => set((state) => ({
        userId
      })),
      setSandboxMockUserId: (sandboxMockUserId) => set({ sandboxMockUserId }),
      switchContext: (businessId, role, workerId) => set((state) => {
        return {
          currentBusinessId: businessId,
          currentRole: role,
          currentWorkerId: workerId,
          isClientMode: role === 'client'
        };
      }),
      setTermsAccepted: (termsAccepted) => set({ termsAccepted }),
      setFeatureFlags: (featureFlags) => set({ featureFlags }),
    }),
    { 
      name: 'app-settings',
      version: 2,
      storage: createJSONStorage(() => safeLocalStorage),
      migrate: (persisted: any, version: number) => {
        if (version < 2) {
          return {
            ...persisted,
            currentBusinessId: null,
            currentWorkerId: null,
            sandboxMockUserId: null,
            isClientMode: false,
            currentRole: persisted.realRole || 'owner',
          };
        }
        return persisted as any;
      },
    },
  ),
);

interface UiState {
  hideBottomNav: boolean;
  setHideBottomNav: (hide: boolean) => void;
}

export const useUiStore = create<UiState>((set) => ({
  hideBottomNav: false,
  setHideBottomNav: (hideBottomNav) => set({ hideBottomNav }),
}));

